import {
  Autocomplete,
  TextField,
  Box,
  Chip,
  Checkbox,
  Tooltip,
  Skeleton,
  InputAdornment,
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePartyManager } from './PartyManagerContext';

const ContactSelector = ({ label }) => {
  const {
    partyData,
    personOptions,
    isUIBlocked,
    isPartyManagerLoading,
    handleContactChange,
    handlePrimaryChange,
    contactWarnings,
    handleTransferContactsToEdit,
    getCustomOptionLabel,
    renderCustomOption,
  } = usePartyManager();

  const fieldKey = 'contacts';
  const fieldState = partyData[fieldKey];

  if (!fieldState) {
    return null;
  }

  const selectedSource = fieldState.selected || 'edit';
  const isLocked = selectedSource !== 'edit';
  const selectedContactsData = fieldState[selectedSource] || [];

  const employeePoolIds = new Set(selectedContactsData.map((c) => c.id));
  const employeePool = personOptions.filter((p) => employeePoolIds.has(p._id));

  const contactPersonIds = new Set(
    selectedContactsData.filter((c) => c.contact).map((c) => c.id),
  );
  const value = employeePool.filter((p) => contactPersonIds.has(p._id));
  const primaryContactId =
    selectedContactsData.find((c) => c.primary)?.id || null;

  const warnings = Object.values(contactWarnings).flat();
  const warningTitle =
    warnings.length > 0 ? (
      <div>
        <p>Folgende Personen wurden nicht gefunden:</p>
        <ul>
          {warnings.map((w, i) => (
            <li key={i}>{w}</li>
          ))}
        </ul>
      </div>
    ) : (
      ''
    );

  const tooltipTitle = isLocked
    ? 'Klicken, um diese Auswahl zur Bearbeitung zu übernehmen.'
    : '';

  return (
    <Box sx={{ width: '100%' }}>
      {isPartyManagerLoading ? (
        <Skeleton variant="rounded" width="100%" height={40} />
      ) : (
        <Tooltip title={tooltipTitle} placement="top-start" arrow>
          <Box
            sx={{ width: '100%', cursor: isLocked ? 'pointer' : 'default' }}
            onClick={isLocked ? handleTransferContactsToEdit : undefined}
          >
            <Autocomplete
              multiple
              id="contact-person-select"
              fullWidth
              options={employeePool}
              size="small"
              value={value}
              onChange={(event, newValue) => {
                handleContactChange(newValue);
              }}
              getOptionLabel={getCustomOptionLabel}
              renderOption={renderCustomOption}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    key={`${option._id}-${index}-a`}
                    size="small"
                    label={`${option.firstName ?? ''} ${
                      option.lastName ?? ''
                    }`.trim()}
                    {...getTagProps({ index })}
                    onDelete={() => {
                      const newValue = value.filter(
                        (v) => v._id !== option._id,
                      );
                      handleContactChange(newValue);
                    }}
                    icon={
                      <Tooltip title="Als Hauptansprechpartner festlegen">
                        <Checkbox
                          size="small"
                          icon={<WorkspacePremiumIcon color="disabled" />}
                          checkedIcon={<WorkspacePremiumIcon color="primary" />}
                          checked={primaryContactId === option._id}
                          onChange={() => handlePrimaryChange(option._id)}
                          sx={{ p: 0, mr: 0.5 }}
                          disabled={isUIBlocked || selectedSource !== 'edit'}
                        />
                      </Tooltip>
                    }
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={label}
                  sx={{
                    '& .MuiInputBase-input': {
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(0, 0, 0, 0.38)',
                      '&.MuiInputLabel-shrink': {
                        color: 'rgba(0, 0, 0, 0.87)',
                      },
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {warnings.length > 0 && (
                          <InputAdornment position="end">
                            <Tooltip title={warningTitle}>
                              <WarningAmberIcon color="warning" />
                            </Tooltip>
                          </InputAdornment>
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              disabled={isUIBlocked}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

export default ContactSelector;
